import {useSelectedDoctor} from "@page-containers/booking/utils/useSelectedDoctor";
import {LinkProps} from "next/link";
import {useRouter} from "next/router";
import React, {useCallback, useEffect, useState} from "react";
import {v4} from "uuid";

import {I18nNamespace} from "@i18n";
import {useCurrentFlowId} from "../../../../page-containers/booking/utils/bookingQuery";
import {BookingStep} from "../../../../page-containers/booking/utils/bookingStep";
import {CustomCategory, EventType} from "../../../../utils/analytics/constants";
import {ApptDetails, getEventQueuer, queueEvent} from "../../../../utils/analytics/eventQueue";
import {getFlowId} from "../../../../utils/getFlowId";
import Button from "../../Button";
import {AppointmentTile} from "../components/AppointmentTile";
import Heading from "../Heading";
import {LocationFragment} from "@components/Booking/LocationSelection/__generated__/bookable-location.type";
import {useTranslation} from "@i18n/client";

const MaybeQuit = ({
  location,
  time,
  originUrl,
  bookingStep,
  apptDetails,
}: {
  location: LocationFragment | null;
  time: number | null;
  originUrl: LinkProps["href"];
  bookingStep: BookingStep;
  apptDetails: ApptDetails;
}): React.ReactElement => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const router = useRouter();
  const [viewId] = useState(v4());
  const flowId = useCurrentFlowId();

  const postEvent = getEventQueuer({
    extraData: {
      category: CustomCategory.AppointmentScheduling,
      flowId,
      viewId,
    },
  });

  useEffect(() => {
    if (bookingStep !== BookingStep.LOADING) {
      queueEvent({
        typeId: EventType.QuitFlowStepViewed,
        extraData: {
          bookingStep,
          category: CustomCategory.AppointmentScheduling,
          viewId,
          flowId: getFlowId(),
          apptDetails,
        },
      });
    }
  }, [apptDetails, bookingStep, viewId]);

  const onQuit = useCallback(() => {
    postEvent({
      typeId: EventType.QuitFlowClicked,
      extraData: {
        bookingStep,
        value: "Quit Booking",
      },
    });
  }, [bookingStep, postEvent]);

  const onContinue = useCallback(() => {
    postEvent({
      typeId: EventType.QuitFlowClicked,
      extraData: {
        bookingStep,
        value: "Continue Booking",
      },
    });
    router.back();
  }, [bookingStep, postEvent, router]);

  const doctor = useSelectedDoctor();

  return (
    <div className="flex flex-col items-center gap-8">
      {location && time && (
        <AppointmentTile
          firstName={doctor?.firstName || undefined}
          lastName={doctor?.lastName || undefined}
          credential={doctor?.credential || undefined}
          imageId={doctor?.coverImage?.id || location?.images?.nodes?.[0]?.image?.id || ""}
          locationTimezone={location.timezone}
          street={location.locationAddress?.address?.street || ""}
          city={location.locationAddress?.address?.city || ""}
          time={time}
        />
      )}
      <Heading>{t("Are you sure you want to cancel?", {ns: "web-scheduling"})}</Heading>
      <p className="font-ir">
        {t("If you cancel, your selections will not be saved.", {ns: "web-scheduling"})}
      </p>
      <div className="flex gap-2">
        <Button size="lg" variant="ghost" onClick={onQuit} href={originUrl}>
          {t("Cancel", {ns: "web-scheduling"})}
        </Button>
        <Button size="lg" onClick={onContinue}>
          {t("Continue", {ns: "web-scheduling"})}
        </Button>
      </div>
    </div>
  );
};

export default React.memo(MaybeQuit);
